import React, { useMemo } from 'react';
import BaseSelector, { Option } from 'src/components/selectors/BaseSelector';
import { FloorplanSummaryItem } from 'src/store/hooks/useListFloorplans';
import { findEffectiveFloorplan, goLiveDateString } from './floorplanListHelper';

const LIVE_SUFFIX = 'LIVE';

/**
 * Build the dropdown label.
 *
 * Suffix:
 *  - "(LIVE)" when the backend flagged this entry as the currently-live
 *    floorplan (resolved authoritatively via getLiveFloorplanAtDate).
 *  - "(YYYY-MM-DD)" otherwise — the future go-live date.
 */
const buildLabel = (fp: FloorplanSummaryItem): string => {
  const displayName = fp.name?.trim() || fp.externalId?.trim() || goLiveDateString(fp);
  const suffix = fp.isLive ? LIVE_SUFFIX : goLiveDateString(fp);
  return `${displayName} (${suffix})`;
};

interface FloorplanSelectProps {
  floorplans: FloorplanSummaryItem[];
  isLoading: boolean;
  date: string;
  onChange: (newDate: string) => void;
}

/**
 * Dropdown that lists the floorplan currently in use plus all upcoming
 * floorplans for a store. Uses the shared `BaseSelector` so the visual
 * treatment matches the store picker (search, loading state, sizing).
 *
 * Presentational: the floorplan list is fetched and owned by `ZoneViewer`
 * (which also needs it for auto-selecting the live date) and passed in, so
 * there is a single `listFloorplans` query per store.
 *
 * The backend returns the already-filtered, already-sorted list (effective
 * floorplan first, then future). Selecting a floorplan sets the live date to
 * that floorplan's goLiveDate, which cascades through the existing Zone Map
 * fetch flow. The selected option is derived from the current date — whichever
 * floorplan would be effective on that date — so the dropdown stays in sync
 * when the user types a date manually.
 */
const FloorplanSelect = ({ floorplans, isLoading, date, onChange }: FloorplanSelectProps) => {
  const effectiveForSelectedDate = useMemo(() => findEffectiveFloorplan(floorplans, date), [floorplans, date]);

  // Forward-only: once a floorplan is selected, hide everything scheduled before it.
  // The cutoff is the selected floorplan's own goLiveDate (not the raw date, which
  // would incorrectly drop the effective floorplan itself).
  const visibleFloorplans = useMemo(() => {
    const cutoff = effectiveForSelectedDate ? goLiveDateString(effectiveForSelectedDate) : undefined;
    return cutoff ? floorplans.filter((fp) => goLiveDateString(fp) >= cutoff) : floorplans;
  }, [floorplans, effectiveForSelectedDate]);

  const options: Option[] = useMemo(
    () =>
      visibleFloorplans.map((fp) => ({
        value: fp.floorplanId,
        label: buildLabel(fp),
      })),
    [visibleFloorplans],
  );

  const placeholder =
    options.length === 0
      ? 'No floorplans available'
      : effectiveForSelectedDate
        ? 'Select a floorplan'
        : 'No floorplan effective on this date';

  return (
    <BaseSelector
      label="Floorplan"
      placeholder={placeholder}
      options={options}
      value={effectiveForSelectedDate?.floorplanId ?? undefined}
      onChange={(floorplanId: string) => {
        const picked = floorplans.find((fp) => fp.floorplanId === floorplanId);
        if (picked) {
          onChange(goLiveDateString(picked));
        }
      }}
      loading={isLoading}
    />
  );
};

export default FloorplanSelect;
